import { del, get, patch } from "@/services/axiosUitils";
import { axiosConfig } from "@/services/axiosConfig";
import type {
  SaveBillingPayload,
  SaveInvoicePayload,
  BillingConfig,
  PaymentStatusRecord,
  UpdatePaymentStatusPayload,
  PaymentStatusApiRecord,
} from "@/project/PTS/pages/Billing/BillingInvoice/types";
import {
  normalizeBillingConfigurationList,
  type BillingConfigurationApiRecord,
} from "@/project/PTS/pages/Billing/BillingInvoice/billingListUtils";
import { normalizePaymentStatusList } from "@/project/PTS/pages/Billing/BillingInvoice/paymentStatusUtils";
import {
  extractInvoiceRecords,
  type InvoiceApiRecord,
} from "@/project/PTS/pages/Billing/BillingInvoice/invoiceListUtils";
import {
  normalizeDashboardListing,
  type DashboardListing,
} from "@/project/PTS/pages/Billing/BillingInvoice/dashboardListUtils";
import {
  normalizeInvoiceReminderList,
  type InvoiceReminderRecord,
} from "@/project/PTS/pages/Billing/BillingInvoice/invoiceReminderUtils";
import {
  BILLING_DEFAULT_PAGE_SIZE,
  buildBillingPaginationParams,
  buildPaginatedResult,
  type PaginatedResult,
} from "@/project/PTS/pages/Billing/BillingInvoice/billingPaginationUtils";

class PtsBillingService {
  /**
   * Get billing configuration list
   * GET /pts_app/billing/?page=1&page_size=50
   */
  getBillingConfigurationRecords = async (
    page = 1,
    pageSize = BILLING_DEFAULT_PAGE_SIZE,
    projectId?: string
  ): Promise<PaginatedResult<BillingConfigurationApiRecord>> => {
    const params: Record<string, string | number> = {
      ...buildBillingPaginationParams(page, pageSize),
    };

    if (projectId?.trim()) {
      params.project = projectId.trim();
    }

    const response = await get<unknown>("/pts_app/billing/", {
      params,
    });

    const records = extractBillingRecords(response);

    return buildPaginatedResult(records, response, page, pageSize);
  };

  getBillingConfigurations = async (
    page = 1,
    pageSize = BILLING_DEFAULT_PAGE_SIZE,
    projectId?: string
  ): Promise<PaginatedResult<BillingConfig>> => {
    const result = await this.getBillingConfigurationRecords(
      page,
      pageSize,
      projectId
    );

    return {
      ...result,
      items: normalizeBillingConfigurationList(result.items),
    };
  };

  /**
   * Save billing configuration
   * POST /pts_app/billing/
   */
  saveBillingConfiguration = async (
    payload: SaveBillingPayload
  ): Promise<unknown> => {
    const response = await axiosConfig.post("/pts_app/billing/", payload);
    return response.data;
  };

  /**
   * Update billing configuration
   * PUT /pts_app/billing/{id}/
   */
  updateBillingConfiguration = async (
    id: string,
    payload: SaveBillingPayload
  ): Promise<unknown> => {
    const response = await axiosConfig.put(`/pts_app/billing/${id}/`, payload);
    return response.data;
  };

  /**
   * Delete billing configuration
   * DELETE /pts_app/billing/{id}/
   */
  deleteBillingConfiguration = async (id: string): Promise<unknown> => {
    return await del(`/pts_app/billing/${id}/`);
  };

  /**
   * Save invoice
   * POST /pts_app/invoice/
   */
  saveInvoice = async (payload: SaveInvoicePayload): Promise<unknown> => {
    const response = await axiosConfig.post("/pts_app/invoice/", payload);
    return response.data;
  };

  /**
   * Get invoice preview list
   * GET /pts_app/invoice/?project=&page=1&page_size=50
   */
  getInvoiceRecords = async (
    page = 1,
    pageSize = BILLING_DEFAULT_PAGE_SIZE,
    projectId?: string,
    status?: string
  ): Promise<PaginatedResult<InvoiceApiRecord>> => {
    const params: Record<string, string | number> = {
      ...buildBillingPaginationParams(page, pageSize),
    };

    if (projectId?.trim()) {
      params.project = projectId.trim();
    }

    if (status?.trim()) {
      params.status = status.trim();
    }

    const response = await get<unknown>("/pts_app/invoice/", {
      params,
    });

    const records = extractInvoiceRecords(response);

    return buildPaginatedResult(records, response, page, pageSize);
  };

  /**
   * Update invoice status
   * PATCH /pts_app/invoice/{id}/
   */
  updateInvoiceStatus = async (
    id: string,
    status: string
  ): Promise<unknown> => {
    return await patch(`/pts_app/invoice/${id}/`, { status });
  };

  /**
   * Update invoice number (recurring invoices)
   * PATCH /pts_app/invoice/{id}/update-invoice-number/
   *
   * Uses axiosConfig directly so HTTP errors propagate as rejected promises
   * (the shared `patch` wrapper silently swallows errors).
   */
  updateInvoiceNumber = async (
    id: string,
    invoiceNumber: string
  ): Promise<unknown> => {
    const response = await axiosConfig.patch(
      `/pts_app/invoice/${id}/update-invoice-number/`,
      { invoice_number: invoiceNumber }
    );
    return response.data;
  };

  /**
   * Delete invoice
   * DELETE /pts_app/invoice/{id}/
   */
  deleteInvoice = async (id: string): Promise<unknown> => {
    return await del(`/pts_app/invoice/${id}/`);
  };

  /**
   * Send invoice follow-up
   * GET /pts_app/invoice-followup/{id}/
   */
  sendInvoiceFollowup = async (id: string): Promise<unknown> => {
    return await get(`/pts_app/invoice-followup/${id}/`);
  };

  /**
   * Get billing dashboard listing
   * GET /pts_app/dashboard-listing/?start_date=YYYY-MM-DD&end_date=YYYY-MM-DD&project=&page=1&page_size=50
   */
  getDashboardListing = async (
    startDate?: string,
    endDate?: string,
    projectId?: string,
    page = 1,
    pageSize = BILLING_DEFAULT_PAGE_SIZE,
    status?: string
  ): Promise<DashboardListing> => {
    const params: Record<string, string | number> = {
      ...buildBillingPaginationParams(page, pageSize),
    };

    if (startDate?.trim()) {
      params.start_date = startDate.trim();
    }

    if (endDate?.trim()) {
      params.end_date = endDate.trim();
    }

    if (projectId?.trim()) {
      params.project = projectId.trim();
    }

    if (status?.trim()) {
      params.status = status.trim();
    }

    const response = await get<unknown>("/pts_app/dashboard-listing/", {
      params,
    });

    return normalizeDashboardListing(response, page, pageSize);
  };

  /**
   * Get payment status list
   * GET /pts_app/payment-status/?start_date=YYYY-MM-DD&end_date=YYYY-MM-DD&project=&page=1&page_size=50
   */
  getPaymentStatusRecords = async (
    startDate?: string,
    endDate?: string,
    projectId?: string,
    page = 1,
    pageSize = BILLING_DEFAULT_PAGE_SIZE,
    status?: string
  ): Promise<PaginatedResult<PaymentStatusRecord>> => {
    const params: Record<string, string | number> = {
      ...buildBillingPaginationParams(page, pageSize),
    };

    if (startDate?.trim()) {
      params.start_date = startDate.trim();
    }

    if (endDate?.trim()) {
      params.end_date = endDate.trim();
    }

    if (projectId?.trim()) {
      params.project = projectId.trim();
    }

    if (status?.trim()) {
      params.status = status.trim();
    }

    const response = await get<unknown>("/pts_app/payment-status/", {
      params,
    });

    const records = normalizePaymentStatusList(response);

    return buildPaginatedResult(records, response, page, pageSize);
  };

  /**
   * Update payment status
   * PATCH /pts_app/payment-status/{id}/
   */
  updatePaymentStatus = async (
    id: string,
    payload: UpdatePaymentStatusPayload
  ): Promise<unknown> => {
    return await patch(`/pts_app/payment-status/${id}/`, payload);
  };

  /**
   * Get invoice reminder list
   * GET /pts_app/invoice-reminder/
   */
  getInvoiceReminders = async (): Promise<InvoiceReminderRecord[]> => {
    const response = await get<unknown>("/pts_app/invoice-reminder/");
    return normalizeInvoiceReminderList(response);
  };
}

function extractBillingRecords(
  response: unknown
): BillingConfigurationApiRecord[] {
  if (!response) {
    return [];
  }

  const list = Array.isArray(response)
    ? response
    : ((response as { data?: unknown[]; results?: unknown[] })?.data ??
      (response as { results?: unknown[] })?.results ??
      []);

  if (!Array.isArray(list)) {
    return [];
  }

  return list.filter(
    (item): item is BillingConfigurationApiRecord =>
      Boolean(item) && typeof item === "object"
  );
}

export type { BillingConfigurationApiRecord };
export type { InvoiceApiRecord };
export type { PaymentStatusApiRecord };
const instance = new PtsBillingService();
export default instance;
